'use client';

import Image from 'next/image';
import React from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import { validateEmail } from '@/lib/validation/email';
import {
  validatePassword,
  validatePasswordConfirmation,
} from '@/lib/validation/password';
import {
  validatePreferredName,
  validateAge,
  validateConfirmationCode,
} from '@/lib/validation/common';
import PoweredBySerefin from '../components/shared/PoweredBySerefin';
import AnimatedGradientButton from '../components/shared/AnimatedGradientButton';

import styles from './styles.module.css';

interface FormData {
  preferredName: string;
  age: string;
  email: string;
  password: string;
  confirmPassword: string;
  confirmationCode: string;
}

interface FormErrors {
  [key: string]: string | boolean | undefined;
  preferredName?: string;
  age?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  confirmationCode?: string;
  lengthGreaterThanEight?: boolean;
  atLeastOneUppercaseLetter?: boolean;
  atLeastOneLowercaseLetter?: boolean;
  atLeastOneNumberOrSymbol?: boolean;
}

interface PasswordRules {
  lengthGreaterThanEight?: boolean;
  atLeastOneUppercaseLetter?: boolean;
  atLeastOneLowercaseLetter?: boolean;
  atLeastOneNumberOrSymbol?: boolean;
}

interface FormState {
  form: FormData;
  formErrors: FormErrors;
  passwordRules: PasswordRules;
  formTouched: { [key: string]: boolean };
}

export default function SignUp(): React.JSX.Element {
  const router = useRouter();

  const [formState, setFormState] = React.useState<FormState>({
    form: {
      age: '',
      confirmationCode: '',
      confirmPassword: '',
      email: '',
      password: '',
      preferredName: '',
    },
    formErrors: {},
    formTouched: {},
    passwordRules: {},
  });

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [step, setStep] = React.useState<number>(0);

  const [isConfirmationCodeResent, setIsConfirmationCodeResent] =
    React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | false>(false);

  const form = formState.form;
  const formErrors = formState.formErrors;

  const handleChange = (key: keyof FormData, value: string): void => {
    setFormState(prevState => ({
      ...prevState,
      form: {
        ...prevState.form,
        [key]: value,
      },
    }));
  };

  const handlePasswordChange = (value: string): void => {
    const validationResult = validatePassword(value);
    setFormState(prevState => ({
      ...prevState,
      form: {
        ...prevState.form,
        password: value,
      },
      passwordRules: {
        ...(prevState.passwordRules || {}),
        ...validationResult.rules,
      },
    }));
  };

  const validateForm = (): boolean => {
    const stepErrors: FormErrors = {};

    if (step === 0) {
      // Use centralized validators
      const emailError = validateEmail(form.email);
      if (emailError) stepErrors.email = emailError;

      const passwordValidation = validatePassword(form.password);
      if (!passwordValidation.isValid) {
        stepErrors.password = passwordValidation.errors[0];
        stepErrors.lengthGreaterThanEight =
          passwordValidation.rules.lengthGreaterThanEight;
        stepErrors.atLeastOneUppercaseLetter =
          passwordValidation.rules.atLeastOneUppercaseLetter;
        stepErrors.atLeastOneLowercaseLetter =
          passwordValidation.rules.atLeastOneLowercaseLetter;
        stepErrors.atLeastOneNumberOrSymbol =
          passwordValidation.rules.atLeastOneNumberOrSymbol;
      }

      const confirmPasswordError = validatePasswordConfirmation(
        form.password,
        form.confirmPassword
      );
      if (confirmPasswordError)
        stepErrors.confirmPassword = confirmPasswordError;

      const preferredNameError = validatePreferredName(form.preferredName);
      if (preferredNameError) stepErrors.preferredName = preferredNameError;

      const ageError = validateAge(form.age);
      if (ageError) stepErrors.age = ageError;
    }

    if (step === 1) {
      const confirmationCodeError = validateConfirmationCode(
        form.confirmationCode
      );
      if (confirmationCodeError)
        stepErrors.confirmationCode = confirmationCodeError;
    }

    setFormState(prevState => ({
      ...prevState,
      formErrors: stepErrors,
    }));

    return Object.keys(stepErrors).length === 0;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    const isValid = validateForm();

    if (isValid) {
      setIsLoading(true);
      try {
        const response = await fetch('/api/auth/register', {
          body: JSON.stringify({
            age: form.age,
            confirmPassword: form.confirmPassword,
            password: form.password,
            preferredName: form.preferredName,
            username: form.email,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Registration failed');
        }
        setStep(1);
      } catch (error: unknown) {
        if (error instanceof Error && error.message === 'User already exists') {
          setFormState(prevState => ({
            ...prevState,
            formErrors: {
              ...prevState.formErrors,
              email: 'User already exists. Please sign in',
            },
          }));
        } else {
          setErrorMessage(
            error instanceof Error ? error.message : 'An unknown error occurred'
          );
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // const handle confirmationCode functionality
  const handleConfirmationCodeSubmission = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    const isValid = validateForm();

    if (isValid) {
      setIsLoading(true);
      try {
        const response = await fetch('/api/auth/verify-email', {
          body: JSON.stringify({
            confirmationCode: form.confirmationCode,
            username: form.email,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        });

        await response.json();
        if (!response.ok) {
          setFormState(prevState => ({
            ...prevState,
            formErrors: {
              confirmationCode:
                "That doesn't look quite right, please try again",
            },
          }));
          return;
        }

        setStep(2);
        setTimeout(() => {
          router.push('/sign-in');
        }, 3000);
      } catch (_error: unknown) {
        setErrorMessage(
          'Unable to resend confirmation code. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResendCode = async (): Promise<void> => {
    try {
      const response = await fetch('/api/auth/resend-confirmation-code', {
        body: JSON.stringify({
          username: form.email,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      setIsConfirmationCodeResent(true);
    } catch (error: unknown) {
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex min-h-screen grow">
        <div className="flex grow items-center justify-center">
          <div className="grid h-full w-full grid-cols-1 md:grid-cols-2">
            {/* Form Section */}
            <div className="flex w-full flex-col p-6 sm:max-w-[600px] sm:justify-self-end md:p-9">
              <Link href="/">
                <Image
                  src="/trustiLogoGradient.svg"
                  className="mb-20"
                  height={38}
                  width={110}
                  alt=""
                />
              </Link>
              <h1 className={`pageHeading ${styles.heading}`}>
                {step === 0 && 'Sign Up'}
                {step === 1 && 'Verification'}
                {step === 2 && (
                  <>
                    Welcome to tru<b>sti</b>™
                  </>
                )}
              </h1>

              {step === 0 && (
                <>
                  <form
                    onSubmit={e => handleSubmit(e)}
                    className="flex flex-col gap-6"
                  >
                    {errorMessage &&
                      errorMessage !==
                        'User already exists. Please sign in' && (
                        <div className={styles.formError}>{errorMessage}</div>
                      )}
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor="preferredName"
                        >
                          Preferred Name
                        </label>
                        {formErrors.preferredName && (
                          <div className={styles.formError}>
                            {formErrors.preferredName}
                          </div>
                        )}
                        <input
                          type="text"
                          id="preferredName"
                          value={form.preferredName}
                          onChange={e =>
                            handleChange('preferredName', e.target.value)
                          }
                          placeholder="Enter your preferred name"
                          className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${formErrors.preferredName ? 'border-red-500' : ''}`}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor="email"
                        >
                          Email
                        </label>
                        {errorMessage ===
                          'User already exists. Please sign in' && (
                          <div className={styles.formError}>{errorMessage}</div>
                        )}
                        {formErrors.email && (
                          <div className={styles.formError}>
                            {formErrors.email}
                          </div>
                        )}
                        <input
                          type="email"
                          id="email"
                          value={form.email}
                          onChange={e => handleChange('email', e.target.value)}
                          placeholder="Enter your email"
                          className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${formErrors.email || errorMessage ? 'border-red-500' : ''}`}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor="age"
                        >
                          Age
                        </label>
                        {formErrors.age && (
                          <div className={styles.formError}>
                            {formErrors.age}
                          </div>
                        )}
                        <input
                          type="text"
                          id="age"
                          value={form.age}
                          onChange={e => handleChange('age', e.target.value)}
                          placeholder="Enter your age"
                          className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${formErrors.age ? 'border-red-500' : ''}`}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor="password"
                        >
                          Password
                        </label>
                        {formErrors.password && (
                          <div className={styles.formError}>
                            {formErrors.password}
                          </div>
                        )}
                        <input
                          type="password"
                          id="password"
                          required
                          value={form.password}
                          onChange={e => handlePasswordChange(e.target.value)}
                          placeholder="Enter your password"
                          className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${formErrors.password ? 'border-red-500' : ''}`}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          className="text-sm font-medium text-gray-700"
                          htmlFor="confirmPassword"
                        >
                          Confirm Password
                        </label>
                        {formErrors.confirmPassword && (
                          <div className={styles.formError}>
                            {formErrors.confirmPassword}
                          </div>
                        )}
                        <input
                          type="password"
                          id="confirmPassword"
                          required
                          value={form.confirmPassword}
                          onChange={e =>
                            handleChange('confirmPassword', e.target.value)
                          }
                          placeholder="Confirm your password"
                          className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${formErrors.confirmPassword ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Password validation rules */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span>
                          {formState.passwordRules &&
                          formState.passwordRules.lengthGreaterThanEight ===
                            true ? (
                            <Image
                              src="/black-checkmark-circle.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          ) : (
                            <Image
                              src="/grey-ellipse.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          )}
                        </span>
                        <span
                          className={`text-xs ${formState.passwordRules && formState.passwordRules.lengthGreaterThanEight === true ? 'text-gray-900' : 'text-gray-400'}`}
                        >
                          Least 8 characters
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span>
                          {formState.passwordRules &&
                          formState.passwordRules.atLeastOneNumberOrSymbol ===
                            true ? (
                            <Image
                              src="/black-checkmark-circle.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          ) : (
                            <Image
                              src="/grey-ellipse.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          )}
                        </span>
                        <span
                          className={`text-xs ${formState.passwordRules && formState.passwordRules.atLeastOneNumberOrSymbol === true ? 'text-gray-900' : 'text-gray-400'}`}
                        >
                          Least one number (0-9) or a symbol
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span>
                          {formState.passwordRules &&
                          formState.passwordRules.atLeastOneUppercaseLetter ===
                            true &&
                          formState.passwordRules &&
                          formState.passwordRules.atLeastOneLowercaseLetter ===
                            true ? (
                            <Image
                              src="/black-checkmark-circle.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          ) : (
                            <Image
                              src="/grey-ellipse.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          )}
                        </span>
                        <span
                          className={`text-xs ${formState.passwordRules && formState.passwordRules.atLeastOneUppercaseLetter === true && formState.passwordRules && formState.passwordRules.atLeastOneLowercaseLetter === true ? 'text-gray-900' : 'text-gray-400'}`}
                        >
                          Lowercase (a-z) and uppercase (a-z)
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span>
                          {form.confirmPassword !== '' &&
                          form.password !== '' &&
                          form.confirmPassword === form.password ? (
                            <Image
                              src="/black-checkmark-circle.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          ) : (
                            <Image
                              src="/grey-ellipse.svg"
                              height={12}
                              width={12}
                              alt=""
                            />
                          )}
                        </span>
                        <span
                          className={`text-xs ${form.confirmPassword !== '' && form.password !== '' && form.confirmPassword === form.password ? 'text-gray-900' : 'text-gray-400'}`}
                        >
                          Passwords match
                        </span>
                      </div>
                    </div>

                    <AnimatedGradientButton
                      as="button"
                      type="submit"
                      theme="purple"
                      flatHover={true}
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Loading' : 'Sign Up'}
                    </AnimatedGradientButton>
                  </form>

                  <div className="mt-8 hidden text-center sm:block">
                    <p className="text-sm text-gray-600">
                      Already have an account?{' '}
                      <Link
                        className="text-blue-600 hover:text-blue-800"
                        href="/sign-in"
                      >
                        Sign in
                      </Link>
                    </p>
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <div
                    className={`${styles.confirmationCodeContainer} w-full max-w-lg`}
                  >
                    <p className={styles.enterTheNumberDescription}>
                      Enter the code we sent you below
                    </p>
                    <div className={styles.resendCodeContainer}>
                      <p>Need a new one?</p>{' '}
                      <button
                        className={styles.textButton}
                        onClick={() => handleResendCode()}
                      >
                        Resend Code
                      </button>
                    </div>

                    {isConfirmationCodeResent && (
                      <p className={styles.codeSentMessage}>
                        Confirmation code has been sent
                      </p>
                    )}
                    <form
                      className="w-full"
                      onSubmit={e => handleConfirmationCodeSubmission(e)}
                    >
                      {formErrors.confirmationCode && (
                        <div className={styles.formError}>
                          {formErrors.confirmationCode}
                        </div>
                      )}

                      <input
                        type="text"
                        id="confirmationCode"
                        value={form.confirmationCode}
                        onChange={e =>
                          handleChange('confirmationCode', e.target.value)
                        }
                        placeholder="Enter verification code"
                        className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${formErrors.confirmationCode ? 'border-red-500' : ''}`}
                      />

                      <AnimatedGradientButton
                        as="button"
                        theme="purple"
                        flatHover={true}
                        className="mt-6 h-[64px] w-full rounded-lg p-3 sm:max-w-[240px]"
                        onClick={() => {
                          const fakeEvent = {
                            preventDefault: () => {},
                          } as React.FormEvent<HTMLFormElement>;
                          handleConfirmationCodeSubmission(fakeEvent);
                        }}
                        disabled={form.confirmationCode === '' || isLoading}
                      >
                        {isLoading ? 'Loading' : 'Validate'}
                      </AnimatedGradientButton>
                    </form>

                    <div className={styles.helpNeededContainer}>
                      <p>
                        Need some help? Reach out to us! <br />
                        <a href="mailto:contact@trusti.ca">contact@trusti.ca</a>
                      </p>
                      <button
                        className={styles.textButton}
                        onClick={() => setStep(0)}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <div className={styles.accountCreatedContainer}>
                  <p>Your account has been successfuly created!</p>
                  <br />
                  <p id="account-created-message-countdown">
                    You will be redirected to the sign-in page. Please click the
                    link below if you are not redirected to the sign in page a
                    few seconds.
                    <br />
                    <br />
                    Click here to go to the <Link href="/sign-in">
                      Sign In
                    </Link>{' '}
                    Page
                  </p>
                </div>
              )}
            </div>

            {/* Decorative Section (Hidden on Mobile) */}
            <div
              className={`${styles.gradientBackground} flex w-full justify-start px-6 pt-6 pb-16 md:p-9`}
            >
              <div className="flex w-full max-w-md flex-col justify-self-end">
                <PoweredBySerefin
                  className="hidden sm:block sm:self-end"
                  isWhite
                />
                <div className="text-center sm:hidden">
                  <p className="mb-2 text-sm text-white">
                    Already have an account?
                  </p>
                  <Link
                    className="text-sm text-white underline hover:text-gray-200"
                    href="/sign-in"
                  >
                    Sign in
                  </Link>
                </div>
                <div className={styles.serefinContainer}>
                  <PoweredBySerefin />
                  <span className="uppercase">
                    © Trusti Diagnostics Inc. 2024
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
